(() => {
  const DIALOG_SELECTOR = '[data-lapidopia-menu-dialog], [data-lapidopia-cart-dialog]';
  const OPEN_DIALOG_SELECTOR = '[data-lapidopia-menu-dialog][open], [data-lapidopia-cart-dialog][open]';
  const SCROLL_LOCK_CLASS = 'lapidopia-scroll-locked';
  const BUILDER_ROOT_SELECTOR = '[data-stonemine-customizer]';
  const STOREFRONT_LOGO_SELECTOR = '.lapidopia-logo img';
  const DESIGN_HEADER_HIDDEN_CLASS = 'lapidopia-design-shell--header-hidden';
  const BUILDER_SCROLL_SELECTOR = '.customizer-layout, .category-rail, .crystal-grid';
  const openers = new WeakMap();
  const builderScrollers = new WeakSet();

  function setExpanded(opener, isOpen) {
    opener?.setAttribute('aria-expanded', String(isOpen));
  }

  function syncScrollLock() {
    const hasOpenDialog = Boolean(document.querySelector(OPEN_DIALOG_SELECTOR));
    document.documentElement.classList.toggle(SCROLL_LOCK_CLASS, hasOpenDialog);
    document.body.classList.toggle(SCROLL_LOCK_CLASS, hasOpenDialog);
  }

  function isLapidopiaDialog(dialog) {
    return Boolean(dialog?.matches?.(DIALOG_SELECTOR));
  }

  function close(dialog, { restoreFocus = true } = {}) {
    if (!isLapidopiaDialog(dialog)) return false;

    const opener = openers.get(dialog);
    if (dialog.open) dialog.close();
    setExpanded(opener, false);
    openers.delete(dialog);
    syncScrollLock();

    if (restoreFocus && opener?.isConnected) {
      opener.focus({ preventScroll: true });
    }
    return true;
  }

  function open(dialog, opener) {
    if (!isLapidopiaDialog(dialog) || typeof dialog.showModal !== 'function') return false;

    for (const openDialog of document.querySelectorAll(OPEN_DIALOG_SELECTOR)) {
      if (openDialog !== dialog) close(openDialog, { restoreFocus: false });
    }

    openers.set(dialog, opener);
    if (!dialog.open) dialog.showModal();
    setExpanded(opener, true);
    syncScrollLock();
    return true;
  }

  const controller = Object.freeze({ open, close });
  window.LapidopiaDialog = controller;

  function pathname(value) {
    if (!value) return null;
    try {
      const path = new window.URL(value, window.location.href).pathname.replace(/\/+$/, '');
      return path || '/';
    } catch {
      return null;
    }
  }

  const BUILDER_CONTROL_KEYS = ['guide', 'gather', 'undo', 'redo', 'clear', 'save'];
  const BUILDER_SEARCH_ID = 'lapidopia-builder-search-input';
  const searchIconListeners = new WeakSet();
  const builderSearchQueries = new WeakMap();

  function builderControlSources(builderRoot) {
    const headerActions = Array.from(builderRoot.querySelectorAll('.topbar-actions button')).slice(-4);
    return {
      guide: builderRoot.querySelector('.guide-trigger'),
      gather: builderRoot.querySelector('.gather-button'),
      undo: headerActions[0],
      redo: headerActions[1],
      clear: headerActions[2],
      save: headerActions[3]
    };
  }

  function copyAttribute(source, target, name) {
    const value = source?.getAttribute?.(name);
    if (value == null || value === '') {
      if (target.getAttribute(name) != null) target.removeAttribute(name);
      return;
    }
    if (target.getAttribute(name) !== value) target.setAttribute(name, value);
  }

  function ensureBuilderControls(builderRoot) {
    const canvasPanel = builderRoot.querySelector('.canvas-panel');
    if (!canvasPanel) return null;

    let controls = builderRoot.querySelector('.lapidopia-builder-controls');
    if (!controls) {
      controls = document.createElement('div');
      controls.classList.add('lapidopia-builder-controls');
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', 'Bracelet controls');
      canvasPanel.prepend(controls);
    }

    for (const key of BUILDER_CONTROL_KEYS) {
      let proxy = Array.from(controls.children).find(node => node.dataset?.lapidopiaBuilderControl === key);
      if (proxy) continue;

      proxy = document.createElement('button');
      proxy.type = 'button';
      proxy.dataset.lapidopiaBuilderControl = key;
      proxy.classList.add('lapidopia-builder-control', `lapidopia-builder-control--${key}`);
      proxy.addEventListener('click', () => {
        const source = builderControlSources(builderRoot)[key];
        if (!source || source.disabled) return;
        source.click();
      });
      controls.append(proxy);
    }

    return controls;
  }

  function syncBuilderControls(builderRoot) {
    const controls = ensureBuilderControls(builderRoot);
    if (!controls) return;
    const sources = builderControlSources(builderRoot);

    for (const proxy of controls.children) {
      const key = proxy.dataset?.lapidopiaBuilderControl;
      const source = sources[key];
      const isMissing = !source;
      if (proxy.hidden !== isMissing) proxy.hidden = isMissing;
      if (isMissing) continue;

      const isDisabled = Boolean(source.disabled);
      if (proxy.disabled !== isDisabled) proxy.disabled = isDisabled;
      copyAttribute(source, proxy, 'aria-disabled');
      copyAttribute(source, proxy, 'title');

      if (key === 'guide') {
        const label = 'Guide & Policies';
        if (proxy.textContent !== label) proxy.textContent = label;
        if (proxy.getAttribute('aria-label') !== label) proxy.setAttribute('aria-label', label);
        continue;
      }

      if (key === 'gather') {
        const sourceLabel = source.textContent?.trim() || 'Gather Bracelet';
        const label = sourceLabel === 'Gather Bracelet'
          ? 'Gather All'
          : sourceLabel === 'Scatter to Tray' ? 'Scatter' : sourceLabel;
        const accessibleLabel = sourceLabel === 'Gather Bracelet' ? 'Gather All' : sourceLabel;
        if (proxy.textContent !== label) proxy.textContent = label;
        if (proxy.getAttribute('aria-label') !== accessibleLabel) proxy.setAttribute('aria-label', accessibleLabel);
        continue;
      }

      const label = source.getAttribute('aria-label') || source.getAttribute('title') || key;
      if (proxy.getAttribute('aria-label') !== label) proxy.setAttribute('aria-label', label);
      if (proxy.innerHTML !== source.innerHTML) proxy.innerHTML = source.innerHTML;
    }

    const guideProxy = Array.from(controls.children)
      .find(node => node.dataset?.lapidopiaBuilderControl === 'guide');
    if (sources.guide && guideProxy && !guideProxy.hidden) sources.guide.hidden = true;
  }

  function filterBuilderCrystalCards(builderRoot, query, status) {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    let visibleCount = 0;

    for (const card of builderRoot.querySelectorAll('.crystal-card')) {
      const crystalName = card.querySelector?.('.crystal-card-main strong')?.textContent || '';
      const isMatch = !normalizedQuery || crystalName.toLocaleLowerCase().includes(normalizedQuery);
      const isHidden = !isMatch;
      if (card.hidden !== isHidden) card.hidden = isHidden;
      if (isMatch) visibleCount += 1;
    }

    const isEmpty = normalizedQuery && visibleCount === 0;
    const statusHidden = !isEmpty;
    const statusText = isEmpty ? `No crystals match “${query.trim()}”.` : '';
    if (status.hidden !== statusHidden) status.hidden = statusHidden;
    if (status.textContent !== statusText) status.textContent = statusText;
  }

  function ensureBuilderSearch(builderRoot) {
    const heading = builderRoot.querySelector('.catalog-heading');
    if (!heading) return null;

    let search = builderRoot.querySelector('.lapidopia-builder-search');
    if (!search) {
      search = document.createElement('div');
      search.classList.add('lapidopia-builder-search');

      const label = document.createElement('label');
      label.setAttribute('for', BUILDER_SEARCH_ID);
      label.textContent = 'Search crystals';

      const input = document.createElement('input');
      input.setAttribute('id', BUILDER_SEARCH_ID);
      input.setAttribute('type', 'search');
      input.setAttribute('placeholder', 'Search crystals');
      input.setAttribute('autocomplete', 'off');
      input.value = builderSearchQueries.get(builderRoot) || '';
      input.addEventListener('input', () => {
        const status = search.querySelector?.('.lapidopia-builder-search-status') || search.children?.[2];
        const query = input.value || '';
        builderSearchQueries.set(builderRoot, query);
        if (status) filterBuilderCrystalCards(builderRoot, query, status);
      });

      const status = document.createElement('p');
      status.classList.add('lapidopia-builder-search-status');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.hidden = true;

      search.append(label, input, status);
      heading.prepend(search);
    }

    const input = search.querySelector?.('input') || Array.from(search.children).find(node => node.getAttribute?.('type') === 'search');
    const status = search.querySelector?.('.lapidopia-builder-search-status')
      || Array.from(search.children).find(node => node.classList?.contains('lapidopia-builder-search-status'));
    if (!input || !status) return null;

    const query = builderSearchQueries.get(builderRoot) ?? input.value ?? '';
    if (input.value !== query) input.value = query;
    builderSearchQueries.set(builderRoot, query);

    const searchIcon = builderRoot.querySelector('.catalog-tools button');
    if (searchIcon && !searchIconListeners.has(searchIcon)) {
      searchIcon.addEventListener('click', () => {
        builderRoot.querySelector('.lapidopia-builder-search input')?.focus();
      });
      searchIconListeners.add(searchIcon);
    }

    filterBuilderCrystalCards(builderRoot, query, status);
    return search;
  }

  function syncBuilderCaption(builderRoot) {
    const workspace = builderRoot.querySelector('.canvas-workspace');
    const source = builderRoot.querySelector('.canvas-heading > .canvas-heading-meta');
    if (!workspace || !source) return;

    let caption = builderRoot.querySelector('.lapidopia-builder-caption');
    if (!caption) {
      caption = document.createElement('div');
      caption.classList.add('lapidopia-builder-caption');
      caption.setAttribute('role', 'status');
      caption.setAttribute('aria-live', 'polite');
      workspace.append(caption);
    }
    if (caption.innerHTML !== source.innerHTML) caption.innerHTML = source.innerHTML;
  }

  function syncBuilderHeaderVisibility(builderRoot) {
    const designShell = document.querySelector('.lapidopia-design-shell');
    if (!designShell) return;

    const scrollers = Array.from(builderRoot.querySelectorAll(BUILDER_SCROLL_SELECTOR));
    for (const scroller of scrollers) {
      if (builderScrollers.has(scroller)) continue;
      scroller.addEventListener('scroll', () => syncBuilderHeaderVisibility(builderRoot), { passive: true });
      builderScrollers.add(scroller);
    }

    const isScrolled = scrollers.some(scroller => scroller.scrollTop > 1);
    designShell.classList.toggle(DESIGN_HEADER_HIDDEN_CLASS, isScrolled);
  }

  function syncBuilderChrome(builderRoot) {
    const aboutPath = pathname(window.__STONEMINE_ROUTES__?.about);
    if (aboutPath) {
      for (const link of builderRoot.querySelectorAll('.desktop-nav a')) {
        const linkPath = pathname(link.href || link.getAttribute?.('href'));
        if (linkPath === aboutPath) link.remove();
      }
    }

    const storefrontLogo = document.querySelector(STOREFRONT_LOGO_SELECTOR);
    const currentLogoUrl = storefrontLogo?.currentSrc || storefrontLogo?.src;
    for (const selector of ['.tray-center-mark', '.ring-center-mark']) {
      const centerMark = builderRoot.querySelector(selector);
      if (centerMark && currentLogoUrl && centerMark.src !== currentLogoUrl) {
        centerMark.src = currentLogoUrl;
      }
    }

    syncBuilderControls(builderRoot);
    syncBuilderCaption(builderRoot);
    ensureBuilderSearch(builderRoot);
    syncBuilderHeaderVisibility(builderRoot);
  }

  function initializeBuilderChrome() {
    const builderRoot = document.querySelector(BUILDER_ROOT_SELECTOR);
    if (!builderRoot) return;

    const sync = () => syncBuilderChrome(builderRoot);
    sync();
    if (typeof window.MutationObserver === 'function') {
      new window.MutationObserver(sync).observe(builderRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
        attributeFilter: ['disabled', 'aria-disabled', 'aria-label', 'title', 'class', 'src']
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    initializeBuilderChrome();
    const menuDialog = document.querySelector('[data-lapidopia-menu-dialog]');
    const menuOpener = document.querySelector('[data-lapidopia-menu-open]');
    const bagOpener = document.querySelector('[data-lapidopia-bag-open]');

    menuOpener?.addEventListener('click', () => {
      if (menuDialog?.open) controller.close(menuDialog);
      else controller.open(menuDialog, menuOpener);
    });

    bagOpener?.addEventListener('click', event => {
      const cartDialog = document.querySelector('[data-lapidopia-cart-dialog]');
      if (controller.open(cartDialog, bagOpener)) event.preventDefault();
    });

    document.querySelectorAll('[data-lapidopia-menu-close]').forEach(node => {
      node.addEventListener('click', () => controller.close(menuDialog));
    });

    document.querySelectorAll('[data-lapidopia-menu-dialog] a').forEach(link => {
      link.addEventListener('click', () => controller.close(menuDialog, { restoreFocus: false }));
    });

    document.addEventListener('click', event => {
      const closeControl = event.target?.closest?.('[data-lapidopia-cart-close]');
      if (!closeControl) return;
      const cartDialog = closeControl.closest?.('[data-lapidopia-cart-dialog]') || document.querySelector('[data-lapidopia-cart-dialog]');
      controller.close(cartDialog);
    });

    document.addEventListener('cancel', event => {
      if (!isLapidopiaDialog(event.target)) return;
      event.preventDefault();
      controller.close(event.target);
    }, true);

    let gallery;
    try {
      gallery = document.querySelector('[data-lapidopia-gallery]');
    } catch {
      gallery = null;
    }
    const galleryTrack = gallery?.querySelector('[data-lapidopia-gallery-track]');
    if (!galleryTrack) return;

    const galleryCurrent = gallery.querySelector('[data-lapidopia-gallery-current]');
    const galleryPrevious = gallery.querySelector('[data-lapidopia-gallery-step="-1"]');
    const galleryNext = gallery.querySelector('[data-lapidopia-gallery-step="1"]');
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const syncGallery = () => {
      const count = galleryTrack.children.length;
      const index = Math.max(0, Math.min(count - 1, Math.round(galleryTrack.scrollLeft / Math.max(galleryTrack.clientWidth, 1))));
      if (galleryCurrent) galleryCurrent.textContent = `${String(index + 1).padStart(2, '0')} / ${String(count).padStart(2, '0')}`;
      if (galleryPrevious) galleryPrevious.disabled = index === 0;
      if (galleryNext) galleryNext.disabled = index === count - 1;
    };

    galleryTrack.addEventListener('scroll', syncGallery, { passive: true });
    [galleryPrevious, galleryNext].forEach(button => {
      button?.addEventListener('click', () => {
        galleryTrack.scrollBy({
          left: Number(button.dataset.lapidopiaGalleryStep) * galleryTrack.clientWidth,
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      });
    });
    syncGallery();
  });
})();
